import { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  Box,
  IconButton,
  CircularProgress,
  Typography,
  Tooltip,
  Fab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import AddCommentIcon from '@mui/icons-material/AddComment';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { apiRequest } from '../utils/api';

// Set up PDF.js worker - use local worker file from public directory
// This is more reliable than CDN and works offline
// The worker file is copied to public/pdf.worker.min.js during setup
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
console.log('[PDFViewer] pdfjs version:', pdfjs.version, 'workerSrc:', pdfjs.GlobalWorkerOptions.workerSrc);

interface FileComment {
  id: number;
  fileId: number;
  pageNumber: number;
  xPosition: number;
  yPosition: number;
  commentText: string;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  creatorName?: string | null;
}

interface PDFViewerWithCommentsProps {
  fileUrl: string;
  fileId: number;
  jobId: number;
  onCommentClick: (comment: FileComment) => void;
  currentUserId?: number;
  refreshTrigger?: number; // Increment this to trigger a refresh
}

export default function PDFViewerWithComments({
  fileUrl,
  fileId,
  jobId,
  onCommentClick,
  refreshTrigger = 0,
}: PDFViewerWithCommentsProps) {
  console.log('[PDFViewer] render — fileUrl:', fileUrl, 'fileId:', fileId, 'jobId:', jobId);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.5);
  const [rotation, setRotation] = useState<0 | 90 | 180 | 270>(0);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState<FileComment[]>([]);
  const [pageDimensions, setPageDimensions] = useState<Map<number, { width: number; height: number }>>(new Map());
  const [addingComment, setAddingComment] = useState(false);
  const [pendingCommentPosition, setPendingCommentPosition] = useState<{ page: number; x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Load comments
  useEffect(() => {
    const loadComments = async () => {
      // Only load if we have valid IDs
      if (!fileId || !jobId || fileId <= 0 || jobId <= 0) {
        return;
      }

      try {
        const response = await apiRequest(`/jobs/${jobId}/files/${fileId}/comments`, { method: 'GET' });
        setComments(response.data.comments || []);
      } catch (error: any) {
        console.error('Error loading comments:', error);
        // Don't let comment loading errors break the component
        // Just log and continue
        if (error.response?.status === 401) {
          // Auth error - let the interceptor handle it
          return;
        }
      }
    };

    loadComments();
  }, [fileId, jobId, refreshTrigger]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    console.log('[PDFViewer] document loaded successfully, numPages:', numPages);
    setNumPages(numPages);
    setLoading(false);
  };

  const onPageLoadSuccess = (page: any, pageNum: number) => {
    const viewport = page.getViewport({ scale: 1 });
    setPageDimensions((prev) => {
      const newMap = new Map(prev);
      newMap.set(pageNum, { width: viewport.width, height: viewport.height });
      return newMap;
    });
  };

  const handlePageClick = useCallback((event: React.MouseEvent<HTMLDivElement>, pageNum: number) => {
    if (!addingComment) return;

    const pageElement = pageRefs.current.get(pageNum);
    if (!pageElement) return;

    const rect = pageElement.getBoundingClientRect();
    const rx = (event.clientX - rect.left) / rect.width;
    const ry = (event.clientY - rect.top) / rect.height;

    // Convert from displayed (rotated) coords back to original page coords
    let x = rx;
    let y = ry;
    if (rotation === 90) {
      x = ry;
      y = 1 - rx;
    } else if (rotation === 180) {
      x = 1 - rx;
      y = 1 - ry;
    } else if (rotation === 270) {
      x = 1 - ry;
      y = rx;
    }

    const clampedX = Math.max(0, Math.min(1, x));
    const clampedY = Math.max(0, Math.min(1, y));

    setPendingCommentPosition({ page: pageNum, x: clampedX, y: clampedY });
    setAddingComment(false);
  }, [addingComment, rotation]);

  const handleAddComment = async (commentText: string) => {
    if (!pendingCommentPosition || !commentText.trim()) {
      setPendingCommentPosition(null);
      return;
    }

    try {
      const response = await apiRequest(
        `/jobs/${jobId}/files/${fileId}/comments`,
        {
          method: 'POST',
          body: JSON.stringify({
            pageNumber: pendingCommentPosition.page,
            xPosition: pendingCommentPosition.x,
            yPosition: pendingCommentPosition.y,
            commentText: commentText.trim(),
          }),
        }
      );

      setComments((prev) => [...prev, response.data.comment]);
      setPendingCommentPosition(null);
    } catch (error) {
      console.error('Error creating comment:', error);
      setPendingCommentPosition(null);
    }
  };

  const handleCommentIconClick = (comment: FileComment, event: React.MouseEvent) => {
    event.stopPropagation();
    onCommentClick(comment);
  };

  const renderCommentIcons = (pageNum: number) => {
    const pageComments = comments.filter((c) => c.pageNumber === pageNum);
    if (pageComments.length === 0) return null;

    const dimensions = pageDimensions.get(pageNum);
    if (!dimensions) return null;

    const pageElement = pageRefs.current.get(pageNum);
    if (!pageElement) return null;

    return (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        {pageComments.map((comment) => {
          // Stored coords are relative to the unrotated page; transform to displayed coords
          let dx = comment.xPosition;
          let dy = comment.yPosition;
          if (rotation === 90) {
            dx = 1 - dy;
            dy = comment.xPosition;
          } else if (rotation === 180) {
            dx = 1 - comment.xPosition;
            dy = 1 - comment.yPosition;
          } else if (rotation === 270) {
            dx = comment.yPosition;
            dy = 1 - comment.xPosition;
          }
          const x = dx * 100;
          const y = dy * 100;

          return (
            <Tooltip key={comment.id} title={comment.commentText.substring(0, 50) + '...'} arrow>
              <IconButton
                onClick={(e) => handleCommentIconClick(comment, e)}
                sx={{
                  position: 'absolute',
                  left: `${x}%`,
                  top: `${y}%`,
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'auto',
                  color: 'primary.main',
                  bgcolor: 'white',
                  boxShadow: 2,
                  '&:hover': {
                    bgcolor: 'primary.light',
                    color: 'white',
                    transform: 'translate(-50%, -50%) scale(1.1)',
                  },
                  transition: 'all 0.2s',
                  zIndex: 10,
                }}
                size="small"
              >
                <CommentIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        })}
      </Box>
    );
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(numPages, prev + 1));
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={goToPrevPage} disabled={pageNumber <= 1}>
            ‹
          </IconButton>
          <Typography variant="body2">
            Page {pageNumber} of {numPages}
          </Typography>
          <IconButton onClick={goToNextPage} disabled={pageNumber >= numPages}>
            ›
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setScale((prev) => Math.max(0.5, prev - 0.25))} size="small">
            −
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: '60px', textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </Typography>
          <IconButton onClick={() => setScale((prev) => Math.min(3, prev + 0.25))} size="small">
            +
          </IconButton>
          <Tooltip title="Rotate 90°">
            <IconButton
              onClick={() =>
                setRotation((prev) => ((prev + 90) % 360) as 0 | 90 | 180 | 270)
              }
              size="small"
              sx={{ ml: 1 }}
            >
              <RotateRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Fab
            size="small"
            color={addingComment ? 'secondary' : 'primary'}
            onClick={() => setAddingComment(!addingComment)}
            sx={{ ml: 2 }}
          >
            <AddCommentIcon />
          </Fab>
        </Box>
      </Box>

      {/* PDF Viewer */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: 2,
          bgcolor: 'grey.200',
          position: 'relative',
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading PDF...
            </Typography>
          </Box>
        )}

        <Document
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={(error) => console.error('[PDFViewer] document load error:', error)}
          onSourceError={(error) => console.error('[PDFViewer] source error:', error)}
          loading={
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Loading PDF...
              </Typography>
            </Box>
          }
          error={
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="body1" color="error">
                Failed to load PDF
              </Typography>
            </Box>
          }
        >
          {Array.from(new Array(numPages), (_, index) => {
            const pageNum = index + 1;
            const isCurrentPage = pageNum === pageNumber;

            return (
              <Box
                key={`page_${pageNum}`}
                sx={{
                  mb: 2,
                  position: 'relative',
                  display: isCurrentPage ? 'block' : 'none',
                }}
              >
                <Box
                  ref={(el: HTMLDivElement | null) => {
                    if (el) pageRefs.current.set(pageNum, el);
                  }}
                  onClick={(e) => handlePageClick(e, pageNum)}
                  sx={{
                    position: 'relative',
                    cursor: addingComment ? 'crosshair' : 'default',
                    bgcolor: 'white',
                    boxShadow: 3,
                    display: 'inline-block',
                  }}
                >
                  <Page
                    pageNumber={pageNum}
                    scale={scale}
                    rotate={rotation}
                    onLoadSuccess={(page) => onPageLoadSuccess(page, pageNum)}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                  {renderCommentIcons(pageNum)}
                </Box>
              </Box>
            );
          })}
        </Document>

        {addingComment && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'info.main',
              color: 'white',
              p: 1.5,
              borderRadius: 1,
              boxShadow: 3,
              zIndex: 1000,
            }}
          >
            <Typography variant="body2">Click on the PDF to add a comment</Typography>
          </Box>
        )}

        {pendingCommentPosition && (
          <CommentInputDialog
            open={!!pendingCommentPosition}
            onClose={() => setPendingCommentPosition(null)}
            onSave={handleAddComment}
          />
        )}
      </Box>
    </Box>
  );
}

// Simple comment input dialog component
interface CommentInputDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (commentText: string) => void;
}

function CommentInputDialog({ open, onClose, onSave }: CommentInputDialogProps) {
  const [commentText, setCommentText] = useState('');

  useEffect(() => {
    if (open) {
      setCommentText('');
    }
  }, [open]);

  const handleSave = () => {
    if (commentText.trim()) {
      onSave(commentText);
      setCommentText('');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Comment</DialogTitle>
      <DialogContent>
        <TextField
          multiline
          rows={4}
          fullWidth
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Enter your comment..."
          autoFocus
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!commentText.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
